import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  Sync as SyncIcon,
  Storage as DatabaseIcon,
  CloudDownload as DownloadIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';

const SyncStatus = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadSyncStatus();
    // Refresh status more frequently when sync is running
    const refreshInterval = syncStatus?.isRunning ? 3000 : 30000; // Every 3 seconds during sync, 30 seconds otherwise
    const interval = setInterval(loadSyncStatus, refreshInterval);
    return () => clearInterval(interval);
  }, [syncStatus?.isRunning]);

  const loadSyncStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSyncStatus();
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error loading sync status:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerManualSync = async (fullSync = false) => {
    try {
      setSyncing(true);
      await apiService.triggerSync(fullSync);
      
      // Wait a bit then refresh status
      setTimeout(() => {
        loadSyncStatus();
        setSyncing(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error triggering sync:', error);
      setSyncing(false);
    }
  };

  const stopSync = async () => {
    try {
      await apiService.stopSync();
      // Refresh status after stopping
      setTimeout(() => {
        loadSyncStatus();
      }, 1000);
    } catch (error) {
      console.error('Error stopping sync:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nog niet gesynchroniseerd';
    return new Date(dateString).toLocaleString('nl-NL');
  };

  if (loading && !syncStatus) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 1 }}>Database status laden...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <DatabaseIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">
            Database Cache Status
          </Typography>
          {syncStatus?.isRunning && (
            <Chip
              icon={<SyncIcon />}
              label="Synchroniseren..."
              color="primary"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        {syncStatus ? (
          <Stack spacing={2}>
            {/* Status Overview */}
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Overzicht
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip
                  icon={<CheckIcon />}
                  label={`${syncStatus.totalOrdersInDb || 0} orders in cache`}
                  color={syncStatus.totalOrdersInDb > 0 ? 'success' : 'warning'}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Laatste sync: ${formatDate(syncStatus.lastSync)}`}
                  variant="outlined"
                  size="small"
                />
                {syncStatus.lastOrderId && (
                  <Chip
                    label={`Laatste order ID: ${syncStatus.lastOrderId}`}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Quick Actions */}
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Synchronisatie Acties
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                  onClick={() => triggerManualSync(false)}
                  disabled={syncing || syncStatus.isRunning}
                >
                  Incrementele Sync
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={syncing ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={() => triggerManualSync(true)}
                  disabled={syncing || syncStatus.isRunning}
                  color="warning"
                >
                  Volledige Sync
                </Button>
                {syncStatus?.isRunning && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={stopSync}
                  >
                    Stop Sync
                  </Button>
                )}
                <Button
                  size="small"
                  variant="text"
                  onClick={loadSyncStatus}
                  disabled={loading}
                >
                  Status Vernieuwen
                </Button>
              </Stack>
            </Box>

            {/* Notes - show if sync is not running or if it's a progress message */}
            {syncStatus.notes && !syncStatus.isRunning && (
              <Alert 
                severity={syncStatus.notes.includes('errors') ? 'warning' : 'info'} 
                sx={{ mt: 1 }}
              >
                <Typography variant="body2">
                  {syncStatus.notes}
                </Typography>
              </Alert>
            )}

            {/* Performance Info */}
            <Alert severity="success" sx={{ mt: 1 }}>
              <Typography variant="body2">
                De sync service haalt automatisch elk uur nieuwe orders op.
              </Typography>
            </Alert>

            {/* Progress Indicator */}
            {syncStatus?.isRunning && syncStatus?.totalOrdersInWooCommerce && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Synchroniseren...</strong>
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {syncStatus.progressSynced || 0} van {syncStatus.totalOrdersInWooCommerce} orders gesynced
                  {syncStatus.progressPercentage !== null && ` (${syncStatus.progressPercentage}%)`}
                  {syncStatus.progressPage && ` - Pagina ${syncStatus.progressPage}`}
                </Typography>
                {syncStatus.progressPercentage !== null && (
                  <Box sx={{ mt: 1, width: '100%' }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.min(syncStatus.progressPercentage, 100)}%`,
                          height: '100%',
                          backgroundColor: 'primary.main',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Alert>
            )}

            {/* Error Info */}
            {syncStatus.error && (
              <Alert severity="error">
                <Typography variant="body2">
                  <strong>Sync Error:</strong> {syncStatus.error}
                </Typography>
              </Alert>
            )}
            
            {/* Error Details from Notes */}
            {syncStatus.errorDetails && syncStatus.errorDetails.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Sync Errors ({syncStatus.errorDetails.length}):
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {syncStatus.errorDetails.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2" component="span">
                        {error}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
          </Stack>
        ) : (
          <Alert severity="warning">
            Database status niet beschikbaar. Controleer of de backend server draait.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default SyncStatus; 